import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
  useMemo
} from 'react'
import { useAuth as useOIDCAuth } from 'react-oidc-context'
import mixpanel from 'mixpanel-browser'
import { SigninRedirectArgs, SignoutRedirectArgs, User } from 'oidc-client-ts'
import history from '../../lib/history'
import { UPDATE_LAST_LOGIN } from '../../graphql/users'
import { getStudentClient } from '../../lib/clients'
import { createUsers, getSelf, mapOIDCToAuth0User } from '../../lib/authenticationUtils'
import { AdminUserPermission } from '../../generated/graphql'
import { Auth0User, Roles } from '../../types'
import {
  ActiveRole,
  deriveEffectiveUser,
  isDualRoleUser,
  storeActiveRole
} from '../../lib/activeRole'
import { getEnvironment } from '../../lib/environment'

const TOKEN_REFRESH_CHECK_INTERVAL_MS = 30_000 // Check every 30 seconds
const TOKEN_REFRESH_BUFFER_SECONDS = 120 // Refresh 2 minutes before expiry
const TOKEN_REFRESH_COOLDOWN_MS = 30_000 // Minimum gap between renew attempts — caps the renewal rate

/**
 * Remaining life of the user's access token in seconds. Returns undefined
 * when the user or the expiry is unknown. Callers must treat "unknown" as
 * its own case: renew through the cooldown, but never force a signout for
 * it and never let it bypass the cooldown.
 */
const getRemainingTokenSeconds = (user?: User | null): number | undefined => {
  if (!user || typeof user.expires_at !== 'number') return undefined
  return user.expires_at - Math.floor(Date.now() / 1000)
}

export interface AuthContextState {
  user: any // Simplified user type, can be extended later
  isAuthenticated: boolean
  isInitializing: boolean
  signIn: (args?: SigninRedirectArgs | undefined) => void
  signOut: (args?: SignoutRedirectArgs | undefined) => void
  /**
   * Returns a valid access token. Renews the token when less than
   * TOKEN_REFRESH_BUFFER_SECONDS remain or when the expiry is unknown.
   * When a renewal fails and the current token is still valid, the
   * current token comes back and no signout occurs. A
   * signout occurs only when the token is known expired and the renewal
   * fails. Pass { forceRefresh: true } when the server has refused the
   * current token (for example a 401): that skips the freshness check
   * but keeps the cooldown, so a rejecting server cannot cause a storm.
   */
  getTokenSilently: (options?: { forceRefresh?: boolean }) => Promise<string | undefined>
  /** Active persona for dual-role (student + advisor) users; null otherwise. */
  activeRole?: ActiveRole | null
  /** True when the raw token carries both the student and an advisor/admin role. */
  isDualRole?: boolean
  /** Persist and activate a persona for the current dual-role user. */
  setActiveRole?: (role: ActiveRole) => void
}

export const AuthContext = createContext<AuthContextState | null>(null)

export const OIDCProviderWithSession = ({ children }: { children: ReactNode }) => {
  const oidc = useOIDCAuth()
  const {
    isAuthenticated = false,
    isLoading = false,
    activeNavigator = undefined,
    user: oidcUser = null,
    signinRedirect = () => undefined,
    signinSilent = async () => undefined,
    signoutRedirect = () => undefined,
    events = undefined
  } = oidc ?? ({} as any)

  const [isInitializing, setIsInitializing] = useState(true)
  const [isLoadingUser, setIsLoadingUser] = useState(false)
  const [user, setUser] = useState<Auth0User | undefined>(undefined)
  const [activeRole, setActiveRoleState] = useState<ActiveRole | null>(null)
  const [hasFetchedClient, setHasFetchedClient] = useState(false)

  // Circuit breaker: prevents concurrent signinSilent calls and runaway
  // retry loops. This ref stops renew calls that overlap in time. The
  // cooldown stamp below stops renew calls that come too close together in
  // time. The two guards are not redundant.
  const refreshInFlightRef = useRef<Promise<string | undefined> | null>(null)
  // Epoch ms of the last completed renew attempt. Success and failure both
  // set it. An expired token bypasses the cooldown.
  const lastRefreshAttemptAtRef = useRef(0)
  // Epoch ms of the last cooldown warning. Routine suppressions warn at
  // most once per cooldown window; a suppressed forced (post-401) refresh
  // always warns — that is the case worth acting on.
  const lastCooldownWarnAtRef = useRef(0)
  const isLoggingOutRef = useRef(false)

  // The freshest known OIDC user. Token math must not read React render
  // state: the renewed user reaches render state through a long chain
  // (userLoaded event -> reducer -> re-render -> context -> consumers),
  // and a lagging step would make the app renew again on every API
  // request. The identity server can also shorten each renewed token
  // near the end of a login session (session max), so renewals must stay
  // rate-capped no matter how fresh the state is. This ref is one more
  // layer of defense against stale render state. It updates from the
  // state effect, from the userLoaded event, and from each renewal
  // result, so it is current without a re-render.
  const freshUserRef = useRef<User | null>(oidcUser)

  /**
   * One shared guard for all freshUserRef write sites: never replace the
   * current user with an older one, and never store a user without a
   * usable token. A candidate without a numeric expires_at counts as
   * newer — renewals arrive in time order.
   * Note: the callback chain in this file keeps one identity only because
   * react-oidc-context@2.4.0 memoizes signinSilent, signoutRedirect, and
   * events on one stable UserManager. Re-check this on a library upgrade.
   */
  const updateFreshUser = useCallback((candidate?: User | null) => {
    if (!candidate?.access_token) return
    const { current } = freshUserRef
    if (
      current &&
      typeof candidate.expires_at === 'number' &&
      typeof current.expires_at === 'number' &&
      candidate.expires_at < current.expires_at
    ) {
      return
    }
    freshUserRef.current = candidate
  }, [])

  useEffect(() => {
    updateFreshUser(oidcUser)
  }, [oidcUser, updateFreshUser])

  const doSignout = useCallback(() => {
    if (isLoggingOutRef.current) return
    isLoggingOutRef.current = true
    signoutRedirect({
      extraQueryParams: {
        id_token_hint: freshUserRef.current?.id_token || ''
      }
    })
  }, [signoutRedirect])

  /**
   * One shared policy for every failed or refused renewal: keep the
   * current token while it is not known expired, and sign out only when
   * no usable token remains. Returns the token to keep, or undefined
   * after a signout. The live failure path and the dormant
   * silentRenewError safety net both call this, so the policy cannot
   * drift between them. Note: react-oidc-context catches signinSilent
   * errors and resolves with null, so the no-token branch is the usual
   * failure path.
   */
  const keepTokenOrSignOut = useCallback(
    (label: string, error?: Error) => {
      const currentUser = freshUserRef.current
      const remainingSeconds = getRemainingTokenSeconds(currentUser)
      const knownExpired = remainingSeconds !== undefined && remainingSeconds <= 0

      if (currentUser?.access_token && !knownExpired) {
        // eslint-disable-next-line no-console
        console.warn(
          `${label} — the current token stays in use (${remainingSeconds ?? 'unknown'}s remain):`,
          error?.message
        )
        return currentUser.access_token
      }

      // eslint-disable-next-line no-console
      console.error(`${label} — logging out:`, error?.message)
      doSignout()
      return undefined
    },
    [doSignout]
  )

  /**
   * Starts one renewal through the single-flight gate. All renew paths
   * (per-request, forced 401 retry, interval, tab wake) share this
   * function.
   */
  const startTokenRefresh = useCallback(() => {
    const refreshPromise = signinSilent()
      .then((renewedUser: User | null | undefined) => {
        if (renewedUser?.access_token) {
          updateFreshUser(renewedUser)
          return renewedUser.access_token
        }
        // A resolved call without a usable token is a failure too. A user
        // object without access_token must not enter the ref.
        return keepTokenOrSignOut(
          'Token refresh failed',
          new Error('signinSilent returned no usable token')
        )
      })
      .catch((error: Error) => keepTokenOrSignOut('Token refresh failed', error))
      .finally(() => {
        lastRefreshAttemptAtRef.current = Date.now()
        refreshInFlightRef.current = null
      })

    refreshInFlightRef.current = refreshPromise
    return refreshPromise
  }, [signinSilent, updateFreshUser, keepTokenOrSignOut])

  // Track renewals from every source, so the freshness ref
  // never lags behind. The library's own automaticSilentRenew timer is
  // off today; this subscription also guards a future re-enable.
  useEffect(() => {
    if (!events) {
      return () => {} // eslint-disable-line @typescript-eslint/no-empty-function
    }

    const handleUserLoaded = (loadedUser: User) => updateFreshUser(loadedUser)
    const handleUserUnloaded = () => {
      freshUserRef.current = null
    }

    events.addUserLoaded(handleUserLoaded)
    events.addUserUnloaded(handleUserUnloaded)

    return () => {
      events.removeUserLoaded(handleUserLoaded)
      events.removeUserUnloaded(handleUserUnloaded)
    }
  }, [events, updateFreshUser])

  // Do not sign out on each silentRenewError. When two renew
  // calls race (for example across two tabs on the shared, rotated
  // refresh token), Keycloak refuses the loser and the library raises
  // this error, but the winner already stored a fresh token and the
  // session is healthy. keepTokenOrSignOut holds the shared policy. Note:
  // with automaticSilentRenew off, the library cannot raise this event
  // today — the handler stays as a safety net for a future re-enable.
  // Do not "simplify" this back to an unconditional signout — that ends
  // healthy sessions.
  useEffect(() => {
    if (!events) {
      return () => {} // eslint-disable-line @typescript-eslint/no-empty-function
    }

    const handleSilentRenewError = (error: Error) => {
      keepTokenOrSignOut('Silent renew error', error)
    }

    events.addSilentRenewError(handleSilentRenewError)

    return () => {
      events.removeSilentRenewError(handleSilentRenewError)
    }
  }, [events, keepTokenOrSignOut])

  // Proactive renewer: automaticSilentRenew is off in
  // Root.tsx, so this interval is the one renew authority. Every renewal
  // flows through the same single-flight gate and cooldown, so the client
  // starts at most one renewal per token. Near the realm session-max
  // wall, tokens shrink and renewals repeat, but the cooldown caps the
  // rate. Simultaneous token request pairs cannot form in one tab.
  useEffect(() => {
    if (!isAuthenticated) return undefined

    const checkAndRefresh = () => {
      if (isLoggingOutRef.current || refreshInFlightRef.current) return

      const remainingSeconds = getRemainingTokenSeconds(freshUserRef.current)
      // Act on a known expiry only. An unknown-expiry token gives no
      // "near expiry" signal, so this timer would renew it forever — it
      // renews on demand in getTokenSilently instead.
      if (remainingSeconds === undefined) return
      // A known-expired token (for example after machine sleep) renews
      // here too. On-demand callers join this renewal through
      // refreshInFlightRef instead of racing it, and a failed renewal
      // signs the user out cleanly.
      if (remainingSeconds >= TOKEN_REFRESH_BUFFER_SECONDS) return
      if (Date.now() - lastRefreshAttemptAtRef.current < TOKEN_REFRESH_COOLDOWN_MS) return

      startTokenRefresh()
    }

    // Browsers slow timers in hidden tabs. One check on tab wake
    // renews an almost-expired token before the first request needs it.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAndRefresh()
      }
    }

    const intervalId = setInterval(checkAndRefresh, TOKEN_REFRESH_CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isAuthenticated, startTokenRefresh])

  // Provide a token, renewing early through the shared single-flight path.
  // Reads freshUserRef, never render state (see the ref note).
  const getTokenSilently = useCallback(
    async (options?: { forceRefresh?: boolean }) => {
      if (isLoggingOutRef.current) return undefined

      const currentUser = freshUserRef.current
      if (!currentUser?.access_token) return undefined

      const remainingSeconds = getRemainingTokenSeconds(currentUser)

      // A known-fresh token comes back as is. forceRefresh skips this
      // check — the caller has server proof (for example a 401) that the
      // token is bad although the clock says it is fresh.
      if (
        !options?.forceRefresh &&
        remainingSeconds !== undefined &&
        remainingSeconds >= TOKEN_REFRESH_BUFFER_SECONDS
      ) {
        return currentUser.access_token
      }

      // Deduplicate: if a refresh is already in flight, wait for it
      if (refreshInFlightRef.current) {
        return refreshInFlightRef.current
      }

      // Cooldown circuit breaker: an attempt completed a
      // moment ago. Do not start one more unless the token is known
      // expired. The cooldown also applies to forceRefresh — that caps
      // the renewal rate when a server rejects every token.
      const knownExpired = remainingSeconds !== undefined && remainingSeconds <= 0
      const msSinceLastAttempt = Date.now() - lastRefreshAttemptAtRef.current
      if (!knownExpired && msSinceLastAttempt < TOKEN_REFRESH_COOLDOWN_MS) {
        if (
          options?.forceRefresh ||
          Date.now() - lastCooldownWarnAtRef.current >= TOKEN_REFRESH_COOLDOWN_MS
        ) {
          lastCooldownWarnAtRef.current = Date.now()
          // eslint-disable-next-line no-console
          console.warn(
            `Token refresh suppressed by cooldown${
              options?.forceRefresh ? ' after a server 401' : ''
            } — ${remainingSeconds ?? 'unknown'}s remain on the current token`
          )
        }
        return currentUser.access_token
      }

      return startTokenRefresh()
    },
    [startTokenRefresh]
  )

  const handleUserSession = useCallback(
    async (userProfile: any) => {
      let appUserId: number | undefined
      let { firstName } = userProfile
      let { lastName } = userProfile
      let permissions: Array<AdminUserPermission> | undefined

      try {
        setIsLoadingUser(true) // set as isLoadingUser until parses the user object
        const token = await getTokenSilently() // Retrieve the token
        if (token) {
          const client = getStudentClient(() => Promise.resolve(token), isAuthenticated)

          // First, try to create the user
          let createdUser: any = await createUsers(client, userProfile)

          if (!createdUser) {
            // If createUsers returns null, try to get the user with getSelf
            createdUser = await getSelf(client)
          }

          if (createdUser) {
            appUserId = createdUser?.appUserId || createdUser?.id
            permissions =
              createdUser?.effectivePermissions ||
              (createdUser?.permissions as Array<AdminUserPermission>)
            firstName = createdUser?.firstName || firstName
            lastName = createdUser?.lastName || lastName

            // Only apply adminUser or studentUser logic after getSelf
            if (createdUser?.studentUser) {
              firstName =
                createdUser?.studentUser?.displayName ||
                createdUser?.studentUser?.firstName ||
                firstName
              lastName = createdUser?.studentUser?.lastName || lastName
            } else if (createdUser?.adminUser) {
              firstName = createdUser?.adminUser?.firstName || firstName
              lastName = createdUser?.adminUser?.lastName || lastName
            }
          }

          if (createdUser) {
            await client.mutate({
              mutation: UPDATE_LAST_LOGIN
            })

            const roles = [Roles.admin]
            if (
              mixpanel &&
              roles.some((r) =>
                userProfile['https://api.student.civitaslearning.com/roles']?.includes(r)
              )
            ) {
              mixpanel.track('User Logged In', {
                institution: getEnvironment(),
                appUserId,
                isDualRole: isDualRoleUser(
                  userProfile['https://api.student.civitaslearning.com/roles']
                )
              })
            }
          }
          setHasFetchedClient(true)
          setFormattedUser({ ...userProfile, appUserId, permissions, firstName, lastName })
          setIsLoadingUser(false)
        }
      } catch (error: any) {
        let errorType = 'generic'
        if (error.message === 'ACCOUNT_DISABLED' || error.message === 'ACCOUNT_NOT_FOUND') {
          errorType = 'account'
        }
        if (error.message === 'STUDENT_LOGIN_DISABLED') {
          errorType = 'student-login-disabled'
        }
        const emailQueryStringPart = userProfile.email
          ? `&email=${encodeURIComponent(userProfile.email)}`
          : ''
        errorType = encodeURIComponent(errorType)

        setIsLoadingUser(false)
        signoutRedirect({
          post_logout_redirect_uri: `${window.location.origin}?errorType=${errorType}${emailQueryStringPart}`,
          extraQueryParams: {
            id_token_hint: freshUserRef.current?.id_token || ''
          }
        })
      }
    },
    [getTokenSilently, signoutRedirect, isAuthenticated]
  )

  const setFormattedUser = (userProfile: any) => {
    setUser({
      ...userProfile,
      roles: userProfile['https://api.student.civitaslearning.com/roles']
    })
  }

  // A silent renewal must not unmount the app.
  // react-oidc-context flips isLoading around every signinSilent call and
  // sets activeNavigator = 'signinSilent' for that whole span. App.tsx and
  // the route guards render loaders while isInitializing is true, so the
  // old mapping remounted the app and fired a burst of GraphQL queries on
  // every renewal. Keep the activeNavigator check.
  useEffect(() => {
    setIsInitializing((isLoading && activeNavigator !== 'signinSilent') || isLoadingUser)
  }, [isLoading, activeNavigator, isLoadingUser])

  useEffect(() => {
    if (isAuthenticated) {
      // Elimina `code` y `state` de la URL
      const url = new URL(window.location.href)
      history.replace(url.pathname)
    }
  }, [isAuthenticated])

  // Fetch the token and log the client only when authenticated and the client hasn't been fetched yet
  useEffect(() => {
    const fetchAndLogClient = async () => {
      if (oidcUser) {
        const auth0User = mapOIDCToAuth0User(oidcUser)
        await handleUserSession(auth0User)
      }
    }

    if (isAuthenticated && !hasFetchedClient) {
      fetchAndLogClient() // Fetch and log the client after authentication
    }
  }, [isAuthenticated, hasFetchedClient, handleUserSession, oidcUser])

  // Dual-role support: persist and activate a persona for the current identity.
  // Orchestration (cache clear, navigation) lives in useRoleSwitch — this only
  // updates state so the provider stays free of Apollo/router dependencies.
  const setActiveRole = useCallback(
    (role: ActiveRole) => {
      if (user?.sub) {
        storeActiveRole(user.sub, role)
      }
      setActiveRoleState(role)
      mixpanel.register({ activeRole: role })
    },
    [user?.sub]
  )

  const isDualRole = isDualRoleUser(user?.roles)

  // The app only ever sees the persona-scoped user; single-role users (and
  // dual users before initialization) pass through untouched.
  const effectiveUser = useMemo(
    () => (user ? deriveEffectiveUser(user, activeRole) : user),
    [user, activeRole]
  )

  const contextValue = useMemo(
    () => ({
      user: effectiveUser,
      isAuthenticated,
      isInitializing,
      signIn: signinRedirect,
      signOut: signoutRedirect,
      getTokenSilently,
      activeRole,
      isDualRole,
      setActiveRole
    }),
    [
      effectiveUser,
      isAuthenticated,
      isInitializing,
      signinRedirect,
      signoutRedirect,
      getTokenSilently,
      activeRole,
      isDualRole,
      setActiveRole
    ]
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)!
  return context
}
